import {
  CameraIntegration,
  epochTimeOfSelectedEvent,
  contactSupportMessage,
} from './base';
import Bowser from 'bowser';
import dayjs from 'dayjs';
import { post } from '../../helpers/securityService';

export class Solink extends CameraIntegration {
  constructor(...args) {
    super(...args);
    this.cameraProviderName = 'solink';
    this.getBrowserAndOS();
  }

  checkForCredentials() {
    this.isAuthenticated =
      !!this.camera?.accessToken &&
      this.camera?.accessToken !== 'refresh_token_invalid' &&
      this.camera?.accessToken !== 'refresh_token_not_available' &&
      this.camera?.accessToken !== 'solink_location_access_denied';
  }

  getBrowserAndOS() {
    // const deviceInfo = Bowser.getParser(window.navigator.userAgent);
    const { browser, os } = Bowser.parse(window.navigator.userAgent);
    this.browserName = browser.name.toLowerCase();
    this.osName = os.name.toLowerCase();
  }

  async submitCredentials(username, password) {
    const { location } = this.camera;
    const loginCredentials = {
      un: username,
      pw: password,
    };

    const response = await post(
      `security/solink_submit_creds/${location}`,
      loginCredentials,
      { orgId: this.orgId },
    ).catch((err) => {
      console.error(err);
      throw Error(
        'We were unable to authenticate your Solink account.',
      );
    });

    if (response && response === 'Error fetching new video obj after authentication with solink') {
      throw Error(
        'Failed to authenticate your Solink account. Please verify the entered credentials.',
      );
    }
    if ( response && response.accessToken === 'solink_location_access_denied' ) {
      throw Error(
        'Access to the cameras associated with this location has been denied. Please ensure that your Solink account has the necessary permissions to access this location.',
      );
    }

    this.camera.accessToken = response.accessToken;
    this.camera.locationDetails = response.locationDetails;
    this.isAuthenticated = true;
  }

  async fetchThumbnails() {
    const {
      locationDetails: { cameraList },
    } = this.camera;

    const thumbnails = cameraList.map((camera) => ({
      src: camera.thumbnailUrl,
      title: camera.name,
      id: camera.id,
    }));

    if (thumbnails.length === 0) {
      console.warn('No cameras found for this location.');
      throw Error(
        `We were unable to find the camera thumbnails associated with your Solink account. ${contactSupportMessage}`,
      );
    }

    return thumbnails;
  }

  async fetchUrl(url) {
    let firstTsFileEntry = '';

    const { accessToken } = this.camera;

    const headers = {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/x-www-form-urlencoded',
    };

    // const response = await axios.get(url, { headers }).catch(err => {
    //   console.error(err);
    //   throw Error(`We were unable to find the video associated with this event. Please try again later. If this error is reoccurring, ${contactSupportMessage}`);
    // });
    const response = await fetch(url, { headers }).catch((err) => {
      console.error(err);
      throw Error(
        `We were unable to find the video associated with this event.  If this error is reoccurring, ${contactSupportMessage}`,
      );
    });

    const data = await response.text();

    const dataLines = data.split('\n');

    // Find the first entry in the line array
    // that matches the .ts extension
    // break out of the loop once entry is found
    try {
      dataLines.forEach((line) => {
        if (/\.ts$/.test(line)) {
          firstTsFileEntry = line;
          // eslint-disable-next-line no-throw-literal
          throw 'Break';
        }
      });
    } catch (e) {
      if (e !== 'Break') throw e;
    }

    return url;
  }

  async fetchVideo(cameraId, seekTime) {
    // event.time from alerts is in YYYYMMDDHHmmss format but fetchVideo from other pages are already in YYYY-MM-DD HH:mm:ss,
    // so we need to check the format and convert if necessary to ensure YYYY-MM-DD HH:mm:ss format for the video player
    const formattedTime = /^\d{14}$/.test(this.event.time)
      ? dayjs(this.event.time, 'YYYYMMDDHHmmss').format('YYYY-MM-DD HH:mm:ss')
      : this.event.time;
    const nowPlaying = {
      offsetSeconds: 0,
      displayTime: formattedTime,
      seekButtons: true,
    };

    const {
      timezone,
      commonSettings,
      //since cameraList is destructured from locationDetails it will error out without hitting the catch block
      locationDetails: { cameraList, deviceUrl } = {},
    } = this.camera;
    // If video seek button pressed
    if (seekTime) {
      nowPlaying.offsetSeconds += seekTime;
      nowPlaying.displayTime = dayjs(
        nowPlaying.displayTime,
        'YYYY-MM-DD HH:mm:ss',
      )
        .add(seekTime, 'second')
        .format('YYYY-MM-DD HH:mm:ss');
      this.event.time = nowPlaying.displayTime;
    }

    if (!cameraList || !cameraList[0]) {
      console.error('Missing cameraList:', { cameraList });
      throw Error(
        `We were unable to find the cameras associated with your Solink account. ${contactSupportMessage}`,
      );
    }

    if (cameraId)
      nowPlaying.camera = cameraList.find((cam) => cam.id === cameraId);
    else [nowPlaying.camera] = cameraList;

    if (Object.keys(nowPlaying.camera).length === 0) {
      console.error(
        'Could not find the camera associated with the provided camera id in the camera list',
        { cameraId, cameraList },
      );
      throw Error(
        `We were unable to find the video for this camera because this camera was not found to be associated with your Solink account. ${contactSupportMessage}`,
      );
    }

    const { streamEarliestSegmentDate, streamLatestSegmentDate } =
      nowPlaying.camera;

    if (!streamEarliestSegmentDate || !streamLatestSegmentDate) {
      console.error('Stream start and/or end date not found', {
        streamEarliestSegmentDate,
        streamLatestSegmentDate,
      });
      throw Error(
        `We were unable to find the correct start and/or end time of the selected video. ${contactSupportMessage}`,
      );
    }

    const videoDuration = commonSettings?.videoDurationSeconds || 30;
    const videoEndTime = dayjs(nowPlaying.displayTime, 'YYYY-MM-DD HH:mm:ss')
      .add(videoDuration, 'seconds')
      .format('HH:mm:ss');

    nowPlaying.event = this.event;
    nowPlaying.description = `${nowPlaying.displayTime} through ${videoEndTime} from the ${nowPlaying.camera.name} camera`;
    nowPlaying.cameraId = nowPlaying.camera.id;
    nowPlaying.accessToken = this.camera.accessToken;

    if (!nowPlaying.cameraId) {
      console.error(
        'Could not find the camera id from the selected camera needed to update the video player',
        nowPlaying.camera,
      );
      throw Error(
        `We were unable to identify the selected camera in order to display the associated video. ${contactSupportMessage}`,
      );
    }

    const beginEpochMilliSeconds = epochTimeOfSelectedEvent({
      format: 'milli',
      time: nowPlaying.displayTime,
      timezone,
    });
    const endEpochMilliSeconds = dayjs(beginEpochMilliSeconds)
      .add(videoDuration, 'seconds')
      .valueOf();
    if (
      !(
        beginEpochMilliSeconds >= streamEarliestSegmentDate &&
        beginEpochMilliSeconds < streamLatestSegmentDate &&
        endEpochMilliSeconds > streamEarliestSegmentDate &&
        endEpochMilliSeconds <= streamLatestSegmentDate
      )
    ) {
      const earliestAvailableTimestamp = dayjs(
        streamEarliestSegmentDate,
      ).format('MM/DD/YYYY HH:mm:ss');
      const latestAvailableTimestamp = dayjs(streamLatestSegmentDate).format(
        'MM/DD/YYYY HH:mm:ss',
      );
      throw Error(
        `Video is not available from the selected camera for the requested period. Video is available from ${earliestAvailableTimestamp} to ${latestAvailableTimestamp}`,
      );
    }

    const params = {
      begin: beginEpochMilliSeconds,
      end: endEpochMilliSeconds,
      stream: nowPlaying.camera.streamId,
    };
    const url = `${deviceUrl}/cameras/${
      nowPlaying.cameraId
    }/video.m3u8?${new URLSearchParams(params).toString()}`;

    const src = await this.fetchUrl(url);

    // Base video configuration
    nowPlaying.video = {
      sources: [{ src, type: 'application/x-mpegURL' }],
      ...this.commonVideoOptions,
    };

    // Force VHS usage on iOS/Safari instead of native HLS because Safari's native player does not use Video.js's custom
    // code including the part that adds the Authorization Bearer token to the request. 
    if (this.osName === 'ios' || this.browserName === 'safari') {
      // Aggressively force VHS by disabling native HLS support
      nowPlaying.video.html5 = {
        vhs: {
          overrideNative: true,
        },
        nativeVideoTracks: false,
        nativeAudioTracks: false,
        nativeTextTracks: false,
      };
      // Also set at root level to ensure it's picked up
      nowPlaying.video.techOrder = ['html5'];
      nowPlaying.video.enableSourceset = false;
    }

    return nowPlaying;
  }
}
